// Кубический персонаж 3x3x3 + блоки-осколки.

import { BODY_VOX_CUBE } from "./bodyVox";

export const BLOCKS_PER_AXIS = 3;
export const BLOCKS_PER_PLAYER = BLOCKS_PER_AXIS ** 3; // 27
export const BLOCK_SIZE = 0.18; // мировые единицы — куб целиком ~0.54
export const PLAYER_HEIGHT = BLOCK_SIZE * BLOCKS_PER_AXIS;
/** Высота 3×3×3 сетки тела по Z (сервер: NpcHeight) — не путать с PLAYER_HEIGHT. */
export const BODY_VOXEL_HEIGHT_Z = 1.8;
export const BODY_CELL_Z = BODY_VOXEL_HEIGHT_Z / BLOCKS_PER_AXIS;
/** Сферы повреждений тела — синхронно с сервером / mesh.frag MAX_HITS */
export const MAX_BODY_HITS = 24;

export interface PlayerVoxel {
  /** битовая маска, бит i = блок жив. Должна совпадать с серверной. */
  alive: number;
  /** базовый цвет персонажа RGB [0..1] */
  color: [number, number, number];
}

export interface DebrisBlock {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  /** время рождения в секундах */
  born: number;
  color: [number, number, number];
  size: number;
  /**
   * С этого timeAcc включается гравитация и полёт (до — куб «застрял» в теле).
   * Нужен для эффекта: один кадр дыра + красные кубики на месте, затем разлёт.
   */
  physicsArmAt?: number;
  /**
   * Высота пола, о которую отскакивает обломок. Без неё — легаси z=0
   * (арена). На уровнях с полом на высоте (лабиринт ~1.5-3.3 м) спавнер
   * обязан передать floorAtColumn, иначе кубы проваливаются под пол.
   */
  floorZ?: number;
  /**
   * Капля (story: кровь существ): о пол не отскакивает и пыли не поднимает —
   * прилипает и гаснет, в конце жизни не рассыпается песком. Без флага у
   * сотни капель на каждое попадание пол дымил чёрными клубами.
   */
  splat?: boolean;
}

// 2.5 → 1.5: «песочное крошение» — обломок быстро усыхает и рассыпается
// в песчинки (renderer.updateDebris → spawnDustBurst), не валяется кубом.
export const DEBRIS_LIFETIME = 1.5;
export const GRAVITY = 28.0;

/** Осколки от ранений: тот порядок величины, что `cube_size` стен в level-pack (см. LEVEL_PIPELINE). */
export const BODY_DEBRIS_CUBE_SIZE = 0.0625;

/** Размер инстанс-кубов «мяса» = шаг серверной bodyVox-сетки (совпадает с деформацией в mesh.vert). */
export const BODY_FLESH_DEBRIS_CUBE_SIZE = BODY_VOX_CUBE;
/** Тёмно-алая кровь (debris uniform, не палитра стен). */
export const BLOOD_DEBRIS_COLOR: [number, number, number] = [0.68, 0.05, 0.05];
/** Тот же оттенок мяса, что mesh.frag (meatTint / inner voxel). */
export const MEAT_MESH_DEBRIS_COLOR: [number, number, number] = [0.64, 0.065, 0.065];

/** ключи блоков: 0..26 → (bx, by, bz) */
export function blockOffset(idx: number): [number, number, number] {
  const bx = idx % BLOCKS_PER_AXIS;
  const by = Math.floor(idx / BLOCKS_PER_AXIS) % BLOCKS_PER_AXIS;
  const bz = Math.floor(idx / (BLOCKS_PER_AXIS * BLOCKS_PER_AXIS));
  // Центрируем сетку относительно (0,0) в плане XY и от пола в Z
  const cx = (bx - (BLOCKS_PER_AXIS - 1) / 2) * BLOCK_SIZE;
  const cy = (by - (BLOCKS_PER_AXIS - 1) / 2) * BLOCK_SIZE;
  const cz = (bz + 0.5) * BODY_CELL_Z; // снизу вверх, как сервер (полное тело)
  return [cx, cy, cz];
}

export function defaultColor(playerId: number): [number, number, number] {
  // Псевдослучайный, но детерминированный цвет.
  const palette: [number, number, number][] = [
    [0.95, 0.25, 0.25],
    [0.25, 0.55, 0.95],
    [0.30, 0.85, 0.40],
    [0.95, 0.85, 0.25],
    [0.85, 0.45, 0.95],
    [0.30, 0.85, 0.85],
    [0.95, 0.55, 0.25],
    [0.65, 0.65, 0.65],
  ];
  return palette[playerId % palette.length];
}

/** Vec4-пакет для uniform uHits[] (24 сферы). */
export function packBodyHitsUniform(
  hits: readonly { lx: number; ly: number; lz: number; r: number }[],
): Float32Array {
  const out = new Float32Array(MAX_BODY_HITS * 4);
  const n = Math.min(MAX_BODY_HITS, hits.length);
  for (let i = 0; i < n; i++) {
    const h = hits[i]!;
    const o = i * 4;
    out[o] = h.lx;
    out[o + 1] = h.ly;
    out[o + 2] = h.lz;
    out[o + 3] = h.r;
  }
  return out;
}
